import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Coupon, WalletCoupon } from '@jbhi-fi/components/src/types';
import { useCartContext } from '../../utils/cart/context/CartContext';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';
import { CouponContentTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CouponContentTransformer';
import { serviceHelper } from '../../config/service-helper';
import { OnlineCouponsApiClient } from '@jbhi-fi/apis/src/onlineCouponsApi/OnlineCouponsApiClient';
import { CouponContextInterface, CartCoupon } from './types';
import { useFeatureFlag } from '../../hooks/useFeatureFlag/useFeatureFlag';
import { ContentfulCoupon } from '@jbhi-fi/apis/src/contentful/types';
import { useSubscriptionApi } from '../../templates/components/customers/hooks/subscriptionApi';
import { SubscriptionTypes } from '../../templates/components/customers/api/subscriptions/types';
import { TermsAndConditions } from '@jbhi-fi/apis/src/onlineCouponsApi/types';
import {
  generateCouponBarcodes,
  getCouponDiscounts,
  getFilteredCoupons,
  sortApplicableCouponsByDiscount,
} from './utils';
import { getLocal, LocalStorageKeys } from '@jbhi-fi/utils/src/localStorage';

type CouponProviderProps = {
  value?: Omit<CouponContextInterface, 'setIsModalOpen' | 'setAppliedCoupons'>;
  children: React.ReactNode;
};

const country = window.Jbhifi.storeLocation;
export const CouponProviderContext = createContext<CouponContextInterface | null>(null);
const couponApiClient = new OnlineCouponsApiClient({
  baseUrl: `${serviceHelper.themeApisUrl}/coupons`,
  countryCode: country,
});

export const CouponContextProvider = ({ children, value }: CouponProviderProps) => {
  const { cart } = useCartContext();
  // Both filtering and sorting logic will only apply to the cart page.
  const isCartPage = window.location.pathname === '/cart';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCouponCode, setSelectedCouponCode] = useState<string | null>(null);
  const { subscriptions } = useSubscriptionApi();
  const subscription = subscriptions[SubscriptionTypes.perks];
  const [coupons, setCoupons] = useState<Array<Coupon>>([]);

  // Controls the appearance logic for the Spend and Get coupon banner
  const isCouponBannerOfferingEnabled = useFeatureFlag('isCouponBannerOfferingEnabled') ?? false;
  const isCouponBannerEnabled = getLocal(LocalStorageKeys.ShouldShowSpendAndGetBanner, true) ?? true;
  const [shouldShowSpendAndGetBanner, setShouldShowSpendAndGetBanner] = useState<boolean>(isCouponBannerEnabled);

  const { processedCoupons, couponHandles, spendAndGetOffering } = useMemo(() => {
    const appliedCoupons = tryParseJson<Array<CartCoupon>>(cart?.attributes?._coupons) || [];
    const couponsInWallet = tryParseJson<Array<WalletCoupon>>(cart?.attributes?._couponsInWallet) || [];

    const filteredCoupons = getFilteredCoupons(
      subscription?.coupons || value?.coupons || [],
      appliedCoupons,
      isCartPage,
      isCartPage ? couponsInWallet : []
    );

    const spendAndGetCoupons = coupons.filter(coupon => coupon.type === 'SpendAndGet');
    const spendAndGetOffering =
      spendAndGetCoupons.find(coupon => coupon.isApplied) ||
      spendAndGetCoupons.sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime())[0];

    const sortedCoupons = isCartPage
      ? sortApplicableCouponsByDiscount(filteredCoupons, couponsInWallet)
      : getCouponDiscounts(filteredCoupons, couponsInWallet);

    const couponHandles = Array.from(new Set(sortedCoupons.map(coupon => coupon.couponReference)));
    return { processedCoupons: sortedCoupons, couponHandles, spendAndGetOffering };
  }, [cart?.attributes?._coupons, cart?.attributes?._couponsInWallet, subscription?.coupons?.length, coupons.length]);

  const spendAndGetCouponOffering =
    coupons.find(c => c.couponCode === spendAndGetOffering?.couponCode && (c.isApplied ? !!c.minimumSpend : true)) ??
    null;

  const isCouponSurfacingEnabled = useFeatureFlag('isCouponSurfacingEnabled') ?? false;
  const isCouponSurfacingEnabledForAttachSlideout =
    useFeatureFlag('isCouponSurfacingEnabledForAttachSlideout') ?? false;
  const isCouponSurfacingEnabledForCart = useFeatureFlag('isCouponSurfacingEnabledForCart') ?? false;

  const isActive = !!((isCouponSurfacingEnabled ?? false) && couponHandles.length);

  const handleGetTermsAndConditions = async (handles: Array<string>) => {
    const validHandles = handles.filter(handle => /^[a-z0-9]+$/i.test(handle));
    const termsPromises = validHandles.map(async handle => {
      const response = await couponApiClient.getTermsAndConditions(handle);

      if (response.error) {
        console.error('Error finding terms and conditions for coupon:', response.error);
        return undefined;
      }
      return response.data;
    });
    const terms = (await Promise.all(termsPromises)).filter(Boolean) as Array<TermsAndConditions>;

    return terms;
  };

  const {
    data: couponContent,
    isLoading: isQueryLoading,
    isFetched: isFetched,
  } = useQuery({
    enabled: isActive && couponHandles.length > 0,
    queryKey: ['getCouponContent', couponHandles],
    refetchOnWindowFocus: false,
    queryFn: () => handleGetTermsAndConditions(couponHandles),
  });

  const couponContentRecords = couponContent
    ?.map(termsRecord => {
      if (!termsRecord?.content) return null;
      const parsedContent = tryParseJson<ContentfulCoupon>(termsRecord.content);

      if (!parsedContent?.fields) return null;
      return parsedContent;
    })
    .filter(Boolean) as Array<ContentfulCoupon>;

  useQuery({
    queryKey: ['couponsWithContent', couponHandles],
    refetchOnWindowFocus: false,
    enabled: isActive && (couponContent?.length ?? 0) > 0 && couponHandles.length > 0,
    queryFn: () => {
      const transformedCoupons = processedCoupons
        .map(coupon => {
          const content = couponContentRecords?.find(entry => entry.fields?.handle?.includes(coupon.couponReference));

          if (!content) return null;

          return {
            ...coupon,
            type: content?.fields?.couponType,
            discount:
              content?.fields?.couponType === 'SpendAndGet' && spendAndGetOffering?.couponCode === coupon.couponCode
                ? spendAndGetOffering?.discount
                : coupon.discount,
            content: CouponContentTransformer.toCouponContent(coupon.couponDescription, content, coupon.expiryDate),
          };
        })
        .filter(Boolean) as Array<Coupon>;

      return transformedCoupons;
    },
    onSuccess: data => {
      setCoupons(data);
    },
  });
  useEffect(() => {
    const appliedCoupons = tryParseJson<Array<CartCoupon>>(cart?.attributes?._coupons) || [];
    const couponsInWallet = tryParseJson<Array<WalletCoupon>>(cart?.attributes?._couponsInWallet) || [];
    const filteredCoupons =
      getFilteredCoupons(coupons, appliedCoupons, isCartPage, isCartPage ? couponsInWallet : []) || [];
    const sortedCoupons = isCartPage
      ? sortApplicableCouponsByDiscount(filteredCoupons, couponsInWallet)
      : filteredCoupons;
    setCoupons(sortedCoupons);
  }, [cart?.attributes?._coupons, cart?.attributes?._couponsInWallet, subscription?.coupons?.length, coupons.length]);

  const selectedCoupon = coupons.find(c => c.couponCode === selectedCouponCode) ?? null;

  const getSelectedCouponContent = async (couponToFetch: Coupon | null) => {
    if (!couponToFetch) return coupons;

    const exclusion = (await couponApiClient.getExclusions(couponToFetch.couponExternalId.toString())).data;
    if (!exclusion) return;

    let barcodeMap: Map<string, string | undefined> | undefined;
    if (exclusion.canBeRedeemedInStore && !couponToFetch.content?.barcode) {
      barcodeMap = await generateCouponBarcodes([couponToFetch]);
    }
    const updatedCoupons = coupons.map(coupon =>
      coupon.couponExternalId === couponToFetch.couponExternalId
        ? {
            ...coupon,
            content: {
              ...coupon.content,
              ...(barcodeMap && {
                barcode: barcodeMap.get(coupon.couponCode),
              }),
            },
            canBeRedeemedInStore: exclusion?.canBeRedeemedInStore ?? coupon.canBeRedeemedInStore,
            canBeRedeemedOnline: exclusion?.canBeRedeemedOnline ?? coupon.canBeRedeemedOnline,
            redemptionConditions: exclusion?.redemptionConditions,
            discount: exclusion?.benefit?.value ?? coupon.discount,
            minimumSpend:
              exclusion?.redemptionConditions.find(
                condition => condition.comparisonType.toLowerCase() === 'dollarvalue'
              )?.greaterThanOrEqualTo ?? coupon.minimumSpend,
          }
        : coupon
    );
    return updatedCoupons;
  };

  useQuery({
    enabled: isActive && !!selectedCoupon,
    queryKey: ['getSelectedCouponContent', selectedCouponCode],
    refetchOnWindowFocus: false,
    queryFn: () => getSelectedCouponContent(selectedCoupon),
    onSuccess: data => {
      setCoupons(data || coupons);
    },
  });
  useQuery({
    enabled: isActive && !!spendAndGetOffering,
    queryKey: ['getSelectedCouponContent', spendAndGetOffering?.couponCode],
    refetchOnWindowFocus: false,
    queryFn: () => getSelectedCouponContent(spendAndGetOffering),
    onSuccess: data => {
      setCoupons(data || coupons);
    },
  });

  const setSelectedCoupon = (coupon: Coupon | null) => {
    setSelectedCouponCode(coupon?.couponCode ?? null);
  };

  return (
    <CouponProviderContext.Provider
      value={{
        isCouponSurfacingEnabled,
        isCouponSurfacingEnabledForAttachSlideout,
        isCouponSurfacingEnabledForCart,
        coupons,
        isModalOpen,
        isLoading: isLoading || isQueryLoading,
        selectedCoupon,
        isFetched,
        isActive,
        isCartPage,
        shouldShowSpendAndGetBanner: shouldShowSpendAndGetBanner && isCouponBannerOfferingEnabled,
        couponOffering: isCouponBannerOfferingEnabled ? spendAndGetCouponOffering : null,
        setCoupons,
        setSelectedCoupon,
        setIsModalOpen,
        setIsLoading,
        setShouldShowSpendAndGetBanner,
      }}
    >
      {children}
    </CouponProviderContext.Provider>
  );
};
